import React, { useEffect } from "react";
import "../styles/PolicyPage.css";
import ndpcImg from "../assets/images/ndpc.jpeg";

const sections = [
  "Purpose", "Scope", "Data We Collect", "Legal Basis for Processing",
  "How We Use Your Data", "How We Protect Your Data", "Your Data Rights",
  "Data Storage", "Data Retention", "Data Breach Notification",
  "Cookies & Tracking", "International Data Transfer", "Policy Updates", "Contact Us",
];

const PrivacyPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="policy-page">

      {/* Banner */}
      <div className="policy-banner">
        <div className="policy-banner__inner">
          <p className="policy-banner__eyebrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Legal Document
          </p>
          <h1 className="policy-banner__title">Data Privacy <span>Policy</span></h1>
          <p className="policy-banner__sub">
            How Iyconsoft Solutions collects, uses, and protects your personal information across all our digital services.
          </p>
          <div className="policy-banner__meta">
            <span className="policy-banner__meta-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Effective Date: December 30th, 2024
            </span>
            <span className="policy-banner__meta-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Iyconsoft Solutions, Nigeria
            </span>
            <span className="policy-banner__meta-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Last Updated: 30th April, 2025
            </span>
          </div>
        </div>
      </div>

      {/* Layout */}
      <div className="policy-layout">

        {/* Sidebar */}
        <aside className="policy-sidebar">
          <div className="policy-toc">
            <p className="policy-toc__label">Contents</p>
            <ul className="policy-toc__list">
              {sections.map((s, i) => (
                <li key={i}>
                  <a href={`#prv-${i + 1}`}>
                    <span className="policy-toc__num">{i + 1}</span>
                    {s}
                  </a>
                </li>
              ))}
            </ul>
            <div className="policy-toc__divider" />
            <div className="policy-toc__links">
              <a href="/terms" className="policy-toc__link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                Terms of Use
              </a>
              <a href="/contact" className="policy-toc__link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6 6z"/>
                </svg>
                Contact Us
              </a>
            </div>
          </div>

          {/* NDPC image — inside sticky sidebar, stays visible on scroll */}
          <div className="policy-ndpc">
            <img src={ndpcImg} alt="NDPC Registration Certificate" className="policy-ndpc__img" />
          </div>

        </aside>

        {/* Main content */}
        <div className="policy-main">
          <span className="policy-effective">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Effective Date: December 30th, 2024 · Last Updated: April 30th, 2025
          </span>

          {/* Welcome */}
          <div className="policy-section" style={{ background: "linear-gradient(135deg, #f0ebff, #fef0f5)", border: "none" }}>
            <div className="policy-body">
              <p>
                We care about your privacy and the information you share with us. We want you to understand how we are using and protecting the information we collect about you.
                <strong> Iyconsoft Solutions</strong> is respectful of data privacy and adopts best practices in compliance with applicable privacy laws and regulations, including the <strong>Nigeria Data Protection Act (NDPA) 2023</strong> and the <strong>Nigerian Communications Commission (NCC) Consumer Code of Practice Regulations</strong>.
              </p>
            </div>
          </div>

          {/* 1. Purpose */}
          <section className="policy-section" id="prv-1">
            <div className="policy-section__header">
              <span className="policy-section__num">1</span>
              <h2 className="policy-section__title">Purpose</h2>
            </div>
            <div className="policy-body">
              <p>This Privacy Notice explains how <strong>Iyconsoft Solutions</strong> uses the personal data we collect from you when you use our website and digital services including <strong>MyCaller, WhoDeyCall, iTravel, VVPSS, and TeTicket</strong>.</p>
            </div>
          </section>

          {/* 2. Scope */}
          <section className="policy-section" id="prv-2">
            <div className="policy-section__header">
              <span className="policy-section__num">2</span>
              <h2 className="policy-section__title">Scope</h2>
            </div>
            <div className="policy-body">
              <p>This Policy applies to all personal data collected, processed, and stored by Iyconsoft Solutions from clients, employees, business partners, website visitors, and any other stakeholders. It governs data collection through our website, applications, products, services, and offline activities.</p>
            </div>
          </section>

          {/* 3. Data We Collect */}
          <section className="policy-section" id="prv-3">
            <div className="policy-section__header">
              <span className="policy-section__num">3</span>
              <h2 className="policy-section__title">Data We Collect</h2>
            </div>
            <div className="policy-body">
              <p>We collect personal data in the following ways:</p>
              <ul className="policy-list">
                <li><strong>Identity & Contact Data:</strong> Full name, phone number, and email address you provide when registering or contacting us.</li>
                <li><strong>NIN-Linked Data:</strong> For MyCaller and WhoDeyCall, caller names are derived from NIN-linked network registration data held by our telecom partners (Airtel, MTN).</li>
                <li><strong>Transaction Data:</strong> Payment references, booking records, ticket information, and collection data processed through iTravel and TeTicket.</li>
                <li><strong>Voting & Survey Data:</strong> Anonymised participation records from VVPSS elections and surveys.</li>
                <li><strong>Device & Technical Data:</strong> IP address, browser type, and usage data collected automatically when you visit our website.</li>
                <li><strong>Communication Data:</strong> Records of your enquiries and support requests.</li>
              </ul>
            </div>
          </section>

          {/* 4. Legal Basis */}
          <section className="policy-section" id="prv-4">
            <div className="policy-section__header">
              <span className="policy-section__num">4</span>
              <h2 className="policy-section__title">Legal Basis for Processing</h2>
            </div>
            <div className="policy-body">
              <p>We process your personal data based on the following legal grounds under <strong>Section 24 and Section 30 of the Nigeria Data Protection Act (NDPA) 2023</strong>:</p>
              <ul className="policy-list">
                <li><strong>Consent:</strong> When you explicitly agree to the processing of your data.</li>
                <li><strong>Contractual Necessity:</strong> To fulfill our obligations under a contract with you.</li>
                <li><strong>Legal Obligations:</strong> To comply with legal and regulatory requirements.</li>
                <li><strong>Legitimate Interests:</strong> To pursue our legitimate business interests, provided these do not override your rights and freedoms.</li>
              </ul>
              <div className="policy-highlight">
                Under the NDPA 2023, we can collect data only if we have a proper reason and we respect users' rights.
              </div>
            </div>
          </section>

          {/* 5. How We Use Data */}
          <section className="policy-section" id="prv-5">
            <div className="policy-section__header">
              <span className="policy-section__num">5</span>
              <h2 className="policy-section__title">How We Use Your Data</h2>
            </div>
            <div className="policy-body">
              <p>We use the personal data that we collect to communicate with you, deliver what you request, improve our service, and to present other information and business offers that may be of interest.</p>
              <ul className="policy-list">
                <li>Providing and improving our products and services.</li>
                <li>Personalizing your experience and tailoring content.</li>
                <li>Processing transactions and fulfilling contractual obligations.</li>
                <li>Sending marketing and promotional communications (with your consent).</li>
                <li>Complying with legal obligations and enforcing agreements.</li>
              </ul>
              <p style={{ marginTop: "16px" }}><strong>Sharing of Personal Data:</strong></p>
              <ul className="policy-list">
                <li><strong>Service Providers:</strong> Trusted partners who process data on our behalf under strict confidentiality agreements.</li>
                <li><strong>Regulatory Authorities:</strong> To comply with legal or regulatory requirements.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets.</li>
                <li><strong>Telecom Partners:</strong> Airtel Nigeria (MyCaller) and MTN Nigeria (WhoDeyCall) under strict data processing agreements.</li>
              </ul>
            </div>
          </section>

          {/* 6. How We Protect */}
          <section className="policy-section" id="prv-6">
            <div className="policy-section__header">
              <span className="policy-section__num">6</span>
              <h2 className="policy-section__title">How We Protect Your Data</h2>
            </div>
            <div className="policy-body">
              <p>We take appropriate technical and organizational measures to prevent loss, unauthorized access, misuse, disclosure, alteration, and destruction of your personal information.</p>
              <ul className="policy-list">
                <li>All data transmitted uses industry-standard SSL/TLS encryption.</li>
                <li>Access to personal data is restricted to authorised personnel only.</li>
                <li>Our systems undergo regular security assessments.</li>
                <li>Voting data on VVPSS is anonymised — individual votes cannot be linked back to voters.</li>
                <li>We maintain comprehensive audit trails for all data processing activities.</li>
              </ul>
            </div>
          </section>

          {/* 7. Your Rights */}
          <section className="policy-section" id="prv-7">
            <div className="policy-section__header">
              <span className="policy-section__num">7</span>
              <h2 className="policy-section__title">Your Data Rights</h2>
            </div>
            <div className="policy-body">
              <p>As a data subject under the NDPA 2023, you have the following rights:</p>
              <ul className="policy-list">
                <li><strong>Access and Correction:</strong> Request access to or correction of your personal data.</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data, subject to legal and contractual obligations.</li>
                <li><strong>Data Portability:</strong> Receive your data in a portable format.</li>
                <li><strong>Objection and Restriction:</strong> Object to or request restriction of data processing.</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time.</li>
                <li><strong>Lodge a Complaint:</strong> File a complaint with the Nigeria Data Protection Commission (NDPC) or relevant supervisory authority.</li>
              </ul>
            </div>
          </section>

          {/* 8. Storage */}
          <section className="policy-section" id="prv-8">
            <div className="policy-section__header">
              <span className="policy-section__num">8</span>
              <h2 className="policy-section__title">Data Storage</h2>
            </div>
            <div className="policy-body">
              <p>All personal information you provide to us is stored on our secure servers as well as secure physical locations and cloud infrastructure (where applicable) for the purposes of providing seamless services and ensuring business continuity. Whenever your information is transferred to other locations, Iyconsoft Solutions will take all necessary steps to ensure that your data is handled securely and in accordance with this privacy policy.</p>
            </div>
          </section>

          {/* 9. Retention */}
          <section className="policy-section" id="prv-9">
            <div className="policy-section__header">
              <span className="policy-section__num">9</span>
              <h2 className="policy-section__title">Data Retention</h2>
            </div>
            <div className="policy-body">
              <p>We retain your data for as long as is necessary for the purpose(s) for which it was collected. Storage of your data is also determined by legal, regulatory, administrative or operational requirements.</p>
              <ul className="policy-list">
                <li>Subscriber and account data: duration of service + 2 years.</li>
                <li>Transaction and payment records: 7 years (financial regulation compliance).</li>
                <li>Voting records on VVPSS: retained in anonymised form as agreed with the organiser.</li>
                <li>Support and communication records: 3 years.</li>
              </ul>
              <p>Data which is not retained is securely destroyed when it is identified as no longer needed for the purposes for which it was collected.</p>
            </div>
          </section>

          {/* 10. Data Breach */}
          <section className="policy-section" id="prv-10">
            <div className="policy-section__header">
              <span className="policy-section__num">10</span>
              <h2 className="policy-section__title">Data Breach Notification</h2>
            </div>
            <div className="policy-body">
              <p>In the event of a data breach, we will:</p>
              <ul className="policy-list">
                <li>Notify affected individuals promptly.</li>
                <li>Report the breach to the relevant regulatory authorities within the required timeframe.</li>
                <li>Take immediate steps to contain and remediate the breach.</li>
              </ul>
            </div>
          </section>

          {/* 11. Cookies */}
          <section className="policy-section" id="prv-11">
            <div className="policy-section__header">
              <span className="policy-section__num">11</span>
              <h2 className="policy-section__title">Cookies & Tracking</h2>
            </div>
            <div className="policy-body">
              <p>When you use our website, we may use cookies and similar technologies to enhance your browsing experience and collect usage data. Cookies used may be:</p>
              <ul className="policy-list">
                <li><strong>Essential Cookies:</strong> Strictly necessary for the website to function.</li>
                <li><strong>Analytics Cookies:</strong> To help us understand how visitors interact with our site (optional).</li>
                <li><strong>Third-Party Cookies:</strong> From trusted partners to improve service delivery.</li>
              </ul>
              <p>You can manage your cookie preferences through your browser settings.</p>
            </div>
          </section>

          {/* 12. International Transfer */}
          <section className="policy-section" id="prv-12">
            <div className="policy-section__header">
              <span className="policy-section__num">12</span>
              <h2 className="policy-section__title">International Data Transfer</h2>
            </div>
            <div className="policy-body">
              <p>We utilize the appropriate data transfer mechanism or its relevant exceptions to transfer data beyond the borders of Nigeria. Prior to any such transfer, we diligently establish suitable safeguards, ensuring that the recipient adheres to legal frameworks, binding corporate rules, contractual clauses, or certification mechanisms that guarantee an adequate level of protection for personal data, aligned with the provisions outlined in the <strong>NDPA 2023</strong>.</p>
            </div>
          </section>

          {/* 13. Policy Updates */}
          <section className="policy-section" id="prv-13">
            <div className="policy-section__header">
              <span className="policy-section__num">13</span>
              <h2 className="policy-section__title">Policy Updates</h2>
            </div>
            <div className="policy-body">
              <p>We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or for other operational reasons. We will notify you of significant changes via our website or service communications. Please review this policy periodically.</p>
            </div>
          </section>

          {/* 14. Contact */}
          <section className="policy-section" id="prv-14">
            <div className="policy-section__header">
              <span className="policy-section__num">14</span>
              <h2 className="policy-section__title">Contact Us</h2>
            </div>
            <div className="policy-body">
              <p>If you have any questions, concerns or complaints about Iyconsoft Solutions' personal data practices or this privacy policy, please get in touch with our Data Protection Officer:</p>
            </div>
            <div className="policy-contact-box">
              <p className="policy-contact-box__title">Iyconsoft Solutions — Data Protection Officer</p>
              <p className="policy-contact-box__addr">9 Durban Street, Wuse 2, Abuja, Nigeria</p>
              <div className="policy-contact-box__items">
                <a href="mailto:tech@iyconsoft.com" className="policy-contact-box__item">
                  <div className="policy-contact-box__icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <span className="policy-contact-box__text">tech@iyconsoft.com</span>
                  <svg className="policy-contact-box__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
                <a href="tel:+2349063628281" className="policy-contact-box__item">
                  <div className="policy-contact-box__icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6 6z"/>
                    </svg>
                  </div>
                  <span className="policy-contact-box__text">+234 (0) 906-362-8281</span>
                  <svg className="policy-contact-box__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
                <a href="/contact" className="policy-contact-box__item">
                  <div className="policy-contact-box__icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  </div>
                  <span className="policy-contact-box__text">Visit Contact Page</span>
                  <svg className="policy-contact-box__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </main>
  );
};

export default PrivacyPage;
